import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BusinessModelService } from '../../services/api/businessModelService';
import { BusinessModel, BusinessModelCreate } from '../../types/businessModel';

interface BusinessModelState {
  models: BusinessModel[];
  currentModel: BusinessModel | null;
  loading: boolean;
  error: string | null;
}

const initialState: BusinessModelState = {
  models: [],
  currentModel: null,
  loading: false,
  error: null,
};

export const fetchBusinessModels = createAsyncThunk(
  'businessModels/fetchAll',
  async (treeId: string) => {
    const response = await BusinessModelService.getByTreeId(treeId);
    return response;
  }
);

export const fetchBusinessModel = createAsyncThunk(
  'businessModels/fetchById',
  async (id: string) => {
    return BusinessModelService.getById(id);
  }
);

export const createBusinessModel = createAsyncThunk(
  'businessModels/create',
  async (model: BusinessModelCreate) => {
    return BusinessModelService.create(model);
  }
);

export const updateBusinessModel = createAsyncThunk(
  'businessModels/update',
  async ({ id, model }: { id: string; model: BusinessModel }) => {
    return await BusinessModelService.update(id, model);
  }
);

export const deleteBusinessModel = createAsyncThunk('businessModels/delete', async (id: string) => {
  await BusinessModelService.delete(id);
  return id;
});

export const setCurrentModel = createAsyncThunk(
  'businessModels/setCurrent',
  async (model: BusinessModel) => {
    return model;
  }
);

const businessModelSlice = createSlice({
  name: 'businessModels',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = action.payload;
      })
      .addCase(fetchBusinessModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch business models';
      })
      .addCase(fetchBusinessModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessModel.fulfilled, (state, action) => {
        state.loading = false;
        state.currentModel = action.payload;
      })
      .addCase(fetchBusinessModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch business model';
      })
      .addCase(createBusinessModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBusinessModel.fulfilled, (state, action) => {
        state.loading = false;
        state.models.push(action.payload);
        state.currentModel = action.payload;
      })
      .addCase(createBusinessModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create business model';
      })
      .addCase(updateBusinessModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBusinessModel.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.models.findIndex((m) => m.id === action.payload.id);
        if (index !== -1) {
          state.models[index] = action.payload;
        }
        if (state.currentModel?.id === action.payload.id) {
          state.currentModel = action.payload;
        }
      })
      .addCase(updateBusinessModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update business model';
      })
      .addCase(deleteBusinessModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBusinessModel.fulfilled, (state, action) => {
        state.loading = false;
        state.models = state.models.filter((m) => m.id !== action.payload);
        if (state.currentModel?.id === action.payload) {
          state.currentModel = null;
        }
      })
      .addCase(deleteBusinessModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete business model';
      })
      .addCase(setCurrentModel.fulfilled, (state, action) => {
        state.currentModel = action.payload;
      });
  },
});

export default businessModelSlice.reducer;
